/**
 * Google Analytics API Service
 * Handles OAuth authentication and data fetching from Google Analytics
 */

const GOOGLE_OAUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const GOOGLE_TOKEN_URL = "https://oauth2.googleapis.com/token";
const GOOGLE_ANALYTICS_API_BASE = "https://analyticsdata.googleapis.com/v1beta";

class GoogleAnalyticsService {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.redirectUri =
      process.env.REACT_APP_GOOGLE_REDIRECT_URI ||
      `${window.location.origin}/auth/google/callback`;
    this.scope = "https://www.googleapis.com/auth/analytics.readonly";
  }

  // Get OAuth URL for Google login
  getOAuthURL() {
    // Check if client ID is configured
    if (!this.clientId) {
      console.error("Google Analytics Client ID is not configured");
      alert(
        "Google Analytics is niet geconfigureerd. Neem contact op met de beheerder.",
      );
      throw new Error("Google Analytics Client ID not configured");
    }

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      response_type: "code",
      access_type: "offline",
      prompt: "consent",
      state: this.generateState(),
    });

    return `${GOOGLE_OAUTH_URL}?${params.toString()}`;
  }

  // Generate random state for OAuth security
  generateState() {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  // Handle OAuth callback
  async handleOAuthCallback(code) {
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );
      const response = await fetch(
        `${API_URL}/api/connections/google/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code, redirect_uri: this.redirectUri }),
        },
      );

      if (!response.ok) {
        throw new Error("Google OAuth callback failed");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Google OAuth callback error:", error);
      throw error;
    }
  }

  // Get user's Google Analytics accounts
  async getAccounts(accessToken) {
    try {
      const response = await fetch(
        "https://analyticsadmin.googleapis.com/v1beta/accounts",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics accounts");
      }

      const data = await response.json();
      return data.accounts || [];
    } catch (error) {
      console.error("Error fetching Google Analytics accounts:", error);
      throw error;
    }
  }

  // Get properties for an account
  async getProperties(accountId, accessToken) {
    try {
      const response = await fetch(
        `https://analyticsadmin.googleapis.com/v1beta/properties?filter=parent:${accountId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics properties");
      }

      const data = await response.json();
      return data.properties || [];
    } catch (error) {
      console.error("Error fetching Google Analytics properties:", error);
      throw error;
    }
  }

  // Get analytics data
  async getAnalyticsData(
    propertyId,
    accessToken,
    dateRange = "7daysAgo",
    metrics = [
      "sessions",
      "totalUsers",
      "screenPageViews",
      "bounceRate",
      "averageSessionDuration",
      "engagedSessions",
    ],
  ) {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        dateRanges: [
          {
            startDate: dateRange,
            endDate: "today",
          },
        ],
        metrics: metrics.map((metric) => ({ name: metric })),
        dimensions: [{ name: "date" }],
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics data");
      }

      const data = await response.json();
      return this.formatAnalyticsData(data);
    } catch (error) {
      console.error("Error fetching Google Analytics data:", error);
      throw error;
    }
  }

  // Get real-time analytics data
  async getRealTimeData(propertyId, accessToken) {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        metrics: [{ name: "activeUsers" }, { name: "screenPageViews" }],
        dimensions: [{ name: "deviceCategory" }],
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runRealtimeReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics real-time data");
      }

      const data = await response.json();
      return this.formatRealTimeData(data);
    } catch (error) {
      console.error("Error fetching Google Analytics real-time data:", error);
      throw error;
    }
  }

  // Get page analytics
  async getPageAnalytics(
    propertyId,
    accessToken,
    dateRange = "7daysAgo",
    limit = 10,
  ) {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        dateRanges: [
          {
            startDate: dateRange,
            endDate: "today",
          },
        ],
        metrics: [
          { name: "screenPageViews" },
          { name: "sessions" },
          { name: "bounceRate" },
        ],
        dimensions: [{ name: "pagePath" }, { name: "pageTitle" }],
        limit: limit,
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics page data");
      }

      const data = await response.json();
      return this.formatPageAnalytics(data);
    } catch (error) {
      console.error("Error fetching Google Analytics page data:", error);
      throw error;
    }
  }

  // Get traffic sources
  async getTrafficSources(propertyId, accessToken, dateRange = "7daysAgo") {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        dateRanges: [
          {
            startDate: dateRange,
            endDate: "today",
          },
        ],
        metrics: [{ name: "sessions" }, { name: "totalUsers" }],
        dimensions: [{ name: "sessionDefaultChannelGroup" }],
        orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
        limit: 10,
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Analytics traffic sources");
      }

      const data = await response.json();
      return this.formatTrafficSources(data);
    } catch (error) {
      console.error("Error fetching Google Analytics traffic sources:", error);
      throw error;
    }
  }

  // Get device breakdown
  async getDeviceBreakdown(propertyId, accessToken, dateRange = "7daysAgo") {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        dateRanges: [
          {
            startDate: dateRange,
            endDate: "today",
          },
        ],
        metrics: [{ name: "sessions" }, { name: "totalUsers" }],
        dimensions: [{ name: "deviceCategory" }],
        orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch device breakdown");
      }

      const data = await response.json();
      if (!data.rows) return [];

      return data.rows.map((row) => ({
        device: row.dimensionValues[0].value,
        sessions: parseInt(row.metricValues[0].value) || 0,
        users: parseInt(row.metricValues[1].value) || 0,
      }));
    } catch (error) {
      console.error("Error fetching device breakdown:", error);
      return [];
    }
  }

  // Get country breakdown
  async getCountryBreakdown(propertyId, accessToken, dateRange = "7daysAgo") {
    try {
      const requestBody = {
        property: `properties/${propertyId}`,
        dateRanges: [
          {
            startDate: dateRange,
            endDate: "today",
          },
        ],
        metrics: [{ name: "sessions" }, { name: "totalUsers" }],
        dimensions: [{ name: "country" }],
        orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
        limit: 10,
      };

      const response = await fetch(
        `${GOOGLE_ANALYTICS_API_BASE}/properties/${propertyId}:runReport`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch country breakdown");
      }

      const data = await response.json();
      if (!data.rows) return [];

      return data.rows.map((row) => ({
        country: row.dimensionValues[0].value,
        sessions: parseInt(row.metricValues[0].value) || 0,
        users: parseInt(row.metricValues[1].value) || 0,
      }));
    } catch (error) {
      console.error("Error fetching country breakdown:", error);
      return [];
    }
  }

  // Format analytics data for charts
  formatAnalyticsData(data) {
    if (!data.rows) return [];

    return data.rows.map((row) => {
      const formattedRow = {
        date: row.dimensionValues[0].value,
      };

      data.metricHeaders.forEach((header, index) => {
        const value = row.metricValues[index].value;
        // Use parseFloat for rate/duration metrics, parseInt for counts
        if (
          header.name === "bounceRate" ||
          header.name === "averageSessionDuration"
        ) {
          formattedRow[header.name] = parseFloat(value) || 0;
        } else {
          formattedRow[header.name] = parseInt(value) || 0;
        }
      });

      return formattedRow;
    });
  }

  // Format real-time data
  formatRealTimeData(data) {
    if (!data.rows) return { activeUsers: 0, pageViews: 0, devices: {} };

    const result = {
      activeUsers: 0,
      pageViews: 0,
      devices: {},
    };

    data.rows.forEach((row) => {
      const device = row.dimensionValues[0].value;
      const activeUsers = parseInt(row.metricValues[0].value) || 0;
      const pageViews = parseInt(row.metricValues[1].value) || 0;

      result.activeUsers += activeUsers;
      result.pageViews += pageViews;
      result.devices[device] = { activeUsers, pageViews };
    });

    return result;
  }

  // Format page analytics
  formatPageAnalytics(data) {
    if (!data.rows) return [];

    return data.rows.map((row) => ({
      page: row.dimensionValues[0].value,
      title: row.dimensionValues[1].value,
      pageViews: parseInt(row.metricValues[0].value) || 0,
      sessions: parseInt(row.metricValues[1].value) || 0,
      bounceRate: parseFloat(row.metricValues[2].value) || 0,
    }));
  }

  // Format traffic sources
  formatTrafficSources(data) {
    if (!data.rows) return [];

    return data.rows.map((row) => ({
      channel: row.dimensionValues[0].value,
      sessions: parseInt(row.metricValues[0].value) || 0,
      users: parseInt(row.metricValues[1].value) || 0,
    }));
  }

  // Refresh access token
  async refreshToken(refreshToken) {
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );
      const response = await fetch(
        `${API_URL}/api/connections/google/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to refresh Google token");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error refreshing Google token:", error);
      throw error;
    }
  }

  // Check if token is valid
  async validateToken(accessToken) {
    try {
      const response = await fetch(
        `https://oauth2.googleapis.com/tokeninfo?access_token=${accessToken}`,
      );
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export default new GoogleAnalyticsService();
